/* Getting a spreadsheet in, in three steps somebody can follow.
 *
 * Choose the file → check the columns → see what would happen → do it.
 *
 * The dry run is the point. "Import 400 rows, fail on row 7, leave 6 behind"
 * is how people lose an afternoon and stop trusting the tool, so nothing is
 * written until the person has seen, row by row, what will work and what
 * will not — and the dry run is held to predicting the real run exactly.
 */
import { useRef, useState } from 'react'
import type { ErpError } from './api'
import { useErp } from './context'
import type { ImportPreview, ImportResult } from './types'

export function ImportView({ doctype, title, onDone }: {
  doctype: string
  title?: string
  onDone?: () => void
}) {
  const erp = useErp()
  const picker = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [mapping, setMapping] = useState<Record<string, string | null>>({})
  const [result, setResult] = useState<ImportResult | null>(null)
  const [busy, setBusy] = useState<'reading' | 'checking' | 'importing' | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  const choose = async (chosen: File) => {
    setBusy('reading'); setFailed(null); setResult(null); setPreview(null)
    try {
      const answer = await erp.importPreview(doctype, chosen)
      setFile(chosen); setPreview(answer); setMapping(answer.mapping)
    } catch (e) { setFailed((e as ErpError).message) }
    finally { setBusy(null) }
  }

  const go = async (dry: boolean) => {
    if (!file) return
    setBusy(dry ? 'checking' : 'importing'); setFailed(null)
    try { setResult(await erp.importRun(doctype, file, mapping, dry)) }
    catch (e) { setFailed((e as ErpError).message) }
    finally { setBusy(null) }
  }

  const stillMissing = (preview?.required_missing ?? [])
    .filter((f) => !Object.values(mapping).includes(f))

  return (
    <div className="page" style={{ display: 'grid', gap: 16, maxWidth: 900 }}>
      <div>
        <h1>Import {title ?? doctype}</h1>
        <p className="muted small" style={{ marginTop: 4, maxWidth: '62ch' }}>
          Your own spreadsheet, as it is. Column headings do not have to match
          anything — check the guesses below and correct any that are wrong.
        </p>
      </div>

      <section className="card card-pad stack" style={{ gap: 10 }}>
        <strong className="small">1 · The file</strong>
        <input ref={picker} type="file" accept=".csv,.xlsx,.xlsm,.txt" className="sr-only"
               id="import-file"
               onChange={(e) => { const f = e.target.files?.[0]; if (f) void choose(f) }} />
        <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <label className="btn" htmlFor="import-file" style={{ width: 'fit-content' }}>
            {busy === 'reading' ? 'Reading…' : file ? 'Choose a different file' : 'Choose a file'}
          </label>
          {file ? <span className="small muted">{file.name} · {preview?.rows ?? 0} rows</span> : null}
        </div>
        {failed ? <div className="err" role="alert">{failed}</div> : null}
      </section>

      {preview ? (
        <section className="card card-pad stack" data-import="columns" style={{ gap: 10 }}>
          <div className="row-between">
            <strong className="small">2 · The columns</strong>
            {preview.unmatched.length ? (
              <span className="chip chip-warn">{preview.unmatched.length} not recognised</span>
            ) : <span className="chip chip-ok">all recognised</span>}
          </div>
          <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {preview.headers.map((header) => (
              <label key={header} className="field">
                <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>
                  {header || '(no heading)'}
                </span>
                <select className="select" value={mapping[header] ?? ''}
                        onChange={(e) => setMapping((m) => ({ ...m, [header]: e.target.value || null }))}>
                  <option value="">Do not import this column</option>
                  {preview.fields.map((f) => (
                    <option key={f.fieldname} value={f.fieldname}>
                      {f.label || f.fieldname}{f.required ? ' *' : ''}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          {stillMissing.length ? (
            <div className="small" style={{ color: 'var(--warn)' }}>
              Nothing is mapped to {stillMissing.join(', ')}, which every
              {' '}{title ?? doctype} needs. Rows will be refused without it.
            </div>
          ) : null}
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn-primary" disabled={busy !== null} onClick={() => void go(true)}>
              {busy === 'checking' ? 'Checking…' : 'Check every row'}
            </button>
            <span className="small muted">Nothing is written yet.</span>
          </div>
        </section>
      ) : null}

      {result ? (
        <section className="card card-pad stack" data-import="result" style={{ gap: 10 }}>
          <div className="row-between">
            <strong className="small">
              {result.dry_run ? '3 · What would happen' : '3 · What happened'}
            </strong>
            <span className={`chip ${result.failed.length ? 'chip-warn' : 'chip-ok'}`}>
              {result.succeeded} of {result.total}
            </span>
          </div>

          {result.failed.length ? (
            <div style={{ maxHeight: 240, overflow: 'auto' }}>
              <table className="tbl">
                <thead><tr><th style={{ width: 70 }}>Row</th><th>Why not</th></tr></thead>
                <tbody>
                  {result.failed.map((f) => (
                    <tr key={f.row} style={{ cursor: 'default' }}>
                      <td className="num">{f.row}</td>
                      <td className="wrap">{f.why}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="small muted" style={{ margin: 0 }}>
              Every row {result.dry_run ? 'would be accepted' : 'was imported'}.
            </p>
          )}

          {result.dry_run ? (
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" disabled={busy !== null || result.succeeded === 0}
                      onClick={() => void go(false)}>
                {busy === 'importing' ? 'Importing…'
                  : `Import ${result.succeeded} row${result.succeeded === 1 ? '' : 's'}`}
              </button>
              {result.failed.length ? (
                <span className="small muted">
                  The {result.failed.length} above will be left out. Fix them in the
                  spreadsheet and import again — the ones already in will be refused as
                  duplicates rather than doubled.
                </span>
              ) : null}
            </div>
          ) : (
            <div className="row" style={{ gap: 8 }}>
              <button className="btn" onClick={() => { setResult(null); setPreview(null); setFile(null) }}>
                Import another file
              </button>
              {onDone ? <button className="btn btn-primary" onClick={onDone}>See them</button> : null}
            </div>
          )}
        </section>
      ) : null}
    </div>
  )
}
